import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { listSnippets, createSnippet, deleteSnippet as apiDeleteSnippet, listCategories } from "@/lib/api";
import Layout from "@/components/Layout";

export default function Snippets() {
  const [snippets, setSnippets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    content: "",
    category_id: 0,
    platform: "web",
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSnippets();
  }, [selectedCategory]);

  async function loadData() {
    try {
      const [catsData] = await Promise.all([listCategories()]);
      setCategories(catsData);
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function loadSnippets() {
    try {
      const data = await listSnippets(selectedCategory);
      setSnippets(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags.length > 0 ? form.tags : [],
    };
    try {
      await createSnippet(payload);
      toast.success("Snippet created");
      setShowForm(false);
      setForm({
        title: "",
        content: "",
        category_id: 0,
        platform: "web",
        tags: [],
      });
      setTagInput("");
      loadSnippets();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function handleDelete(id) {
    if (!confirm("Delete this snippet?")) return;
    try {
      await apiDeleteSnippet(id);
      toast.success("Snippet deleted");
      loadSnippets();
    } catch (e) {
      toast.error(e.message);
    }
  }

  function addTag() {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
      setTagInput("");
    }
  }

  function removeTag(tag) {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  }

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Snippets</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {showForm ? "Cancel" : "+ New Snippet"}
          </button>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
              selectedCategory === null
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                selectedCategory === cat.id
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid gap-4">
              <input
                type="text"
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full p-2 border rounded"
                required
              />
              <textarea
                placeholder="Content (code or notes)"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="w-full p-2 border rounded font-mono text-sm"
                rows={6}
              />
              <div className="flex gap-4">
                <select
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: Number(e.target.value) })}
                  className="p-2 border rounded"
                >
                  <option value={0}>Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <select
                  value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  className="p-2 border rounded"
                >
                  <option value="web">Web</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                  <option value="desktop">Desktop</option>
                </select>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Add tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  className="flex-1 p-2 border rounded"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-3 py-2 bg-gray-200 rounded"
                >
                  Add
                </button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {form.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="text-indigo-600">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Save Snippet
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : snippets.length === 0 ? (
          <p className="text-gray-500">No snippets yet. Create your first snippet!</p>
        ) : (
          <div className="space-y-3">
            {snippets.map((snippet) => (
              <div
                key={snippet.id}
                className="p-4 border rounded-lg bg-white shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold">{snippet.title}</h3>
                    {snippet.content && (
                      <pre className="mt-2 p-2 bg-gray-50 rounded text-sm overflow-x-auto">
                        {snippet.content}
                      </pre>
                    )}
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs px-2 py-1 rounded bg-gray-100">
                        {categories.find((c) => c.id === snippet.category_id)?.name ||
                          "Uncategorized"}
                      </span>
                      <span className="text-xs px-2 py-1 rounded bg-gray-100">
                        {snippet.platform}
                      </span>
                      {snippet.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(snippet.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}